import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, CircleHelp, Gauge, Loader2, XCircle } from 'lucide-react'
import type { Staat } from '../../gedeeld/types'
import { STAAT_LABEL } from '../lib/opmaak'
import { useThema } from '../lib/thema'
import logoUrl from '../assets/logo.webp'

/* ===========================================================================
 *  De bouwstenen, zoals in Projects -- en één erbij: het merk van een stand.
 *
 *  Een stand is nooit alleen een kleur. Groen en geel zijn voor wie
 *  kleurenblind is nauwelijks uit elkaar te houden (nagerekend met de
 *  palettoets), en in het lichte thema rood en oranje ook niet goed. Dus
 *  overal een eigen icoon, en waar ruimte is het woord erbij.
 * =========================================================================== */

type KnopSoort = 'hoofd' | 'gewoon' | 'stil' | 'gevaar'

export function Knop({
  soort = 'gewoon', klein, bezig, icoon, children, className, ...rest
}: {
  soort?: KnopSoort
  klein?: boolean
  bezig?: boolean
  icoon?: ReactNode
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button"
      className={`knop k-${soort} ${klein ? 'klein' : ''} ${children ? '' : 'alleen-icoon'} ${className ?? ''}`}
      disabled={rest.disabled || bezig} {...rest}>
      {bezig ? <Loader2 size={15} className="draai" /> : icoon}
      {children}
    </button>
  )
}

export function Logo({ breedte = 96 }: { breedte?: number }) {
  const licht = useThema().actief === 'licht'
  return (
    <span className={`logo-vlak ${licht ? 'op-licht' : ''}`} style={{ width: breedte }}>
      <img src={logoUrl} alt="Truckwash1 Group" width={breedte} height={Math.round((breedte / 250) * 70)} draggable={false} />
    </span>
  )
}

export function Badge({ kleur = 'info', children, title }: { kleur?: string; children: ReactNode; title?: string }) {
  return <span className={`badge t-${kleur}`} title={title}>{children}</span>
}

export function Laden({ tekst }: { tekst?: string }) {
  return (
    <div className="laden">
      <Loader2 size={18} className="draai" />
      {tekst && <span>{tekst}</span>}
    </div>
  )
}

export function Leeg({ icoon, titel, tekst, children }: { icoon?: ReactNode; titel: string; tekst?: ReactNode; children?: ReactNode }) {
  return (
    <div className="leeg">
      {icoon && <div className="leeg-icoon">{icoon}</div>}
      <h3>{titel}</h3>
      {tekst && <p>{tekst}</p>}
      {children && <div className="leeg-acties">{children}</div>}
    </div>
  )
}

export function Foutvlak({ fout, opnieuw }: { fout: string; opnieuw?: () => void }) {
  return (
    <div className="foutvlak" role="alert">
      <span>{fout}</span>
      {opnieuw && <Knop klein onClick={opnieuw}>Opnieuw</Knop>}
    </div>
  )
}

export function Tabs<T extends string>({ waarde, tabs, onKies }: {
  waarde: T
  tabs: { key: T; label: string; icoon?: ReactNode; teller?: number }[]
  onKies: (t: T) => void
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={t.key === waarde}
          className={`tab ${t.key === waarde ? 'actief' : ''}`} onClick={() => onKies(t.key)}>
          {t.icoon}
          <span>{t.label}</span>
          {t.teller !== undefined && t.teller > 0 && <span className="teller">{t.teller}</span>}
        </button>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Een stand: icoon, kleur, woord
 * ------------------------------------------------------------------ */

export const STAAT_KLEUR: Record<Staat, string> = { werkt: 'ok', traag: 'warn', storing: 'danger', onbekend: 'grijs' }

export function StaatIcoon({ s, grootte = 16 }: { s: Staat; grootte?: number }) {
  const p = { size: grootte, 'aria-hidden': true as const, className: `staat-icoon s-${s}` }
  if (s === 'werkt') return <CheckCircle2 {...p} />
  if (s === 'traag') return <AlertTriangle {...p} />
  if (s === 'storing') return <XCircle {...p} />
  return <CircleHelp {...p} />
}

/** Het merk met het woord erbij: voor koppen en tabellen. */
export function StaatMerk({ s, klein }: { s: Staat; klein?: boolean }) {
  return (
    <span className={`staat-merk s-${s} ${klein ? 'klein' : ''}`}>
      <StaatIcoon s={s} grootte={klein ? 13 : 15} />
      {STAAT_LABEL[s]}
    </span>
  )
}

/** Een getal met een label eronder: de tellers bovenaan het overzicht. */
export function Teller({ s, n, label, onClick, actief }: { s: Staat | 'alle'; n: number; label: string; onClick?: () => void; actief?: boolean }) {
  return (
    <button type="button" className={`teller-tegel s-${s} ${actief ? 'actief' : ''}`} onClick={onClick} aria-pressed={actief}>
      <span className="teller-boven">
        {s === 'alle' ? <Gauge size={16} aria-hidden /> : <StaatIcoon s={s} />}
        <span className="teller-getal">{n}</span>
      </span>
      <span className="teller-label">{label}</span>
    </button>
  )
}
